/**
 * Software & Blueprints Step - Infrastructure templates and Docker containers
 */

import { useState } from 'react';
import { BlueprintRecommendations } from './BlueprintRecommendations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  Container, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  Shield,
  Info,
  Copy,
  Server
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';

const DEFAULT_REGISTRY = 'registry.dell.com/ai-hub';

export function SoftwareBlueprintsStep() {
  const [registryUrl, setRegistryUrl] = useState(DEFAULT_REGISTRY);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [username, setUsername] = useState('');
  const [token, setToken] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleTestConnection = async () => {
    if (!registryUrl) {
      toast({
        title: 'Registry URL Required',
        description: 'Please enter a Docker registry URL',
        variant: 'destructive',
      });
      return;
    }

    if (requiresAuth && (!username || !token)) {
      toast({
        title: 'Authentication Required',
        description: 'Please provide username and token',
        variant: 'destructive',
      });
      return;
    }

    setTestingConnection(true);
    setConnectionStatus('idle');

    // Simulate connection test (in production, this would be a real API call)
    await new Promise(resolve => setTimeout(resolve, 2000));

    // For now, always succeed since we can't actually test without backend
    setConnectionStatus('success');
    setTestingConnection(false);
    
    toast({
      title: 'Connection Successful',
      description: `Successfully connected to ${registryUrl}`,
    });
  };

  const handleCopySetup = () => {
    const setupText = `
Docker Registry Configuration
=============================
Registry URL: ${registryUrl}
Authentication: ${requiresAuth ? 'Required' : 'Not Required'}
${requiresAuth ? `Username: ${username}` : ''}

To configure Docker CLI:
${requiresAuth ? `docker login ${registryUrl} -u ${username}` : `# No authentication needed for ${registryUrl}`}
    `.trim();

    navigator.clipboard.writeText(setupText);
    toast({
      title: 'Configuration Copied',
      description: 'Docker registry setup copied to clipboard',
    });
  };

  return (
    <div className="space-y-6">
      {/* Docker Registry Configuration */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Container className="h-5 w-5 text-primary" />
                Docker Registry Configuration
              </CardTitle>
              <CardDescription>
                Configure access to Dell's AI model registry for container downloads
              </CardDescription>
            </div>
            {connectionStatus === 'success' && (
              <Badge variant="outline" className="gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                Connected
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Configure your Docker registry to pull pre-built AI model containers optimized for Dell infrastructure.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="registry-url">Registry URL</Label>
              <div className="flex gap-2">
                <Input
                  id="registry-url"
                  value={registryUrl}
                  onChange={(e) => setRegistryUrl(e.target.value)}
                  placeholder="registry.dell.com/ai-hub"
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopySetup}
                  title="Copy configuration"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Default Dell AI Hub registry. Contact your Dell representative for custom registries.
              </p>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="requires-auth" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Requires Authentication
                </Label>
                <p className="text-xs text-muted-foreground">
                  Enable if your registry requires login credentials
                </p>
              </div>
              <Switch
                id="requires-auth"
                checked={requiresAuth}
                onCheckedChange={setRequiresAuth}
              />
            </div>

            {requiresAuth && (
              <div className="space-y-4 p-4 rounded-lg bg-muted/50 border border-border">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="your-username"
                    autoComplete="username"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="token">Access Token / Password</Label>
                  <Input
                    id="token"
                    type="password"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="your-access-token"
                    autoComplete="current-password"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use a personal access token or password for registry authentication
                  </p>
                </div>
              </div>
            )}

            <Button
              onClick={handleTestConnection}
              disabled={testingConnection}
              className="w-full"
              variant="outline"
            >
              {testingConnection ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testing Connection...
                </>
              ) : connectionStatus === 'success' ? (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Connection Successful
                </>
              ) : connectionStatus === 'error' ? (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  Connection Failed - Retry
                </>
              ) : (
                <>
                  <Server className="mr-2 h-4 w-4" />
                  Test Connection
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Blueprint Recommendations */}
      <BlueprintRecommendations />
    </div>
  );
}
